Ext.define('SA.VectorMapExportController', {
    constructor(model) {
        this.model = model;

        this.beginExport();
    },

    awaitLegend() {
        var {model} = this;

        return new Promise(resolve => {
            maybeResolve();

            function maybeResolve() {
                var attribute = model.getAttribute(),
                    legendBreaks = model.getLegendBreaks();

                if (attribute && legendBreaks === false) {
                    model.on({'change:legend': {fn: maybeResolve, single: true}});
                }
                else {
                    resolve();
                }
            }
        });
    },

    async beginExport() {
        if (this.model.hasUserAttributeWithoutUnitsAtCurrentScale()) {
            this.showAlert([
                'We are unable to export your map because there is no data on your current map. ',
                'Please zoom in to a location where you’ve uploaded your data and try again.'
            ]);

            return;
        }

        Ext.getBody().mask('Waiting for legend…');
        await this.awaitLegend();
        Ext.getBody().unmask();

        if (this.model.getLegendBreaks() !== null) {
            this.showCroppingWindow();
        }
        else {
            this.showAlert([
                'We are unable to export your map because there is not enough variation in your data ',
                '(for example, all or most locations have the same value). ',
                'Please select a different data variable for your map export.'
            ]);
        }
    },

    beforeOverlayEditWindowDestroy(win) {
        this.onOverlayLeftBlur(win.down('#left'));
        this.onOverlayTopBlur(win.down('#top'));
        this.onOverlayWidthBlur(win.down('#width'));
        this.onOverlayHeightBlur(win.down('#height'));

        win.overlay.editWindow = null;
    },

    beforeTextEditWindowDestroy(win) {
        this.onTextLeftBlur(win.down('#left'));
        this.onTextTopBlur(win.down('#top'));
        this.onTextTextBlur(win.down('#text'));

        win.textLabel.editWindow = null;
    },

    destroyExportViews: SA.createBuffered(() => {
        ['vector-map-export-cropping-window', 'vector-map-export-window', 'vector-map-export-delivery-options-window'].forEach(cq => {
            Ext.destroy(Ext.ComponentQuery.query(cq));
        });
    }, 10),

    destroyTextLabel(view) {
        var label = this.getTextOverlay(view),
            details = this.getExport(view);

        setTimeout(() => {
            Ext.destroy(this.getLabelEditWindow(view));

            details.removeTextLabel(label);

            Ext.tip.QuickTipManager.getQuickTip().hide();
        }, 10);
    },

    getCropAspectRatio(view) {
        var combo = this.getCropAspectRatioCombo(view),
            orientation = this.getCropOrientation(view),
            ratio = this.getPaperSize(combo.getValue()).getAspectRatio();

        if (orientation !== 'portrait') {
            ratio = 1 / ratio;
        }

        return ratio;
    },

    getCropAspectRatioCombo(view) {
        return this.getCroppingWindow(view).down('vector-map-export-crop-aspect-ratio-fieldset #aspectRatio');
    },

    getCropBox(view) {
        return this.getCroppingWindow(view).down('vector-map-export-crop-box');
    },

    getCropOrientation(view) {
        var radio = this.getCroppingWindow(view).down('vector-map-export-crop-aspect-ratio-fieldset #orientation');

        return radio.getValue();
    },

    getCroppingWindow(view) {
        var cq = 'vector-map-export-cropping-window';

        return view.is(cq) ? view : view.up(cq) || Ext.ComponentQuery.query(cq)[0];
    },

    getDeliveryOptionsWindow(view) {
        var cq = 'vector-map-export-delivery-options-window';

        return view.is(cq) ? view : view.up(cq) || Ext.ComponentQuery.query(cq)[0];
    },

    getExport(view) {
        return (this.getExportWindow(view) || this.getCroppingWindow(view)).details;
    },

    getExportWindow(view) {
        var cq = 'vector-map-export-window';

        return view.is(cq) ? view : view.up(cq) || Ext.ComponentQuery.query(cq)[0];
    },

    getLockAspectRatioButton(view) {
        return this.getExportWindow(view).down('#lock-aspect-ratio-button');
    },

    getMap(view) {
        return this.getExportWindow(view).map;
    },

    getOrientation(view) {
        var radio = this.getExportWindow(view).down('export-orientation-radiogroup radio[value]'),
            details;

        if (radio) {
            return radio.getSubmitValue();
        }

        details = this.getExport(view);

        return details.getHeight() > details.getWidth() ? 'portrait' : 'landscape';
    },

    getOverlayEditWindow(view) {
        return view.up('export-overlay-edit-window');
    },

    getPaperSize(name) {
        return SA.model.map.PaperSize.sizes[name];
    },

    getPreviewPage(view) {
        return this.getExportWindow(view).down('vector-map-export-preview-page');
    },

    getScaleBar(view) {
        return this.getPreviewPage(view).down('vector-map-export-scale-bar-overlay');
    },

    getLabelEditWindow(view) {
        return view.up('export-label-edit-window');
    },

    getTextOverlay(view) {
        return this.getLabelEditWindow(view).record;
    },

    getThemeRadioGroup(view) {
        return this.getExportWindow(view).down('#legendTheme');
    },

    getThemeRadioGroupLabel(view) {
        return this.getExportWindow(view).down('#legendThemeRadioGroupLabel');
    },

    isCropLockAspectRatio(view) {
        return !this.getCroppingWindow(view).down('vector-map-export-crop-aspect-ratio-fieldset').collapsed;
    },

    onAddTextLabelClick(button) {
        Ext.widget({
            xtype: 'export-add-text-label-window',

            handler: text => {
                if (text) {
                    this.getExport(button).addTextLabel({
                        background: 'FFFFFF',
                        bold: false,
                        color: '000000',
                        italic: false,
                        size: 24,
                        text,
                        typeface: 'Verdana',
                        underline: false,
                        x: Math.random() * 0.5 + 0.25,
                        y: Math.random() * 0.5 + 0.25
                    });
                }
            }
        });
    },

    onContinueToExportClick(btn) {
        var exportWindow = this.getExportWindow(btn),
            deliveryWindow = this.getDeliveryOptionsWindow(btn);

        if (!deliveryWindow) {
            deliveryWindow = Ext.widget({
                xtype: 'vector-map-export-delivery-options-window',
                listeners: {destroy: this.destroyExportViews, scope: this}
            });

            this.registerChildEventListeners(deliveryWindow, {
                'sa-button[action=cancelMapExport]': {
                    click: this.destroyExportViews
                },

                '#finishedButton': {
                    click: this.onFinishedClick
                },

                '#returnToLayoutButton': {
                    click: this.onReturnToLayoutClick
                }
            });
        }

        deliveryWindow.show();

        exportWindow.hide();
    },

    onCropContinueClick(btn) {
        var details = this.getExport(btn),
            croppingWindow = this.getCroppingWindow(btn),
            map = details.getMap(),
            insetMap = details.getInsetMap(),
            aspectRatio = details.getExtent().getAspectRatio(),
            exportWindow = this.getExportWindow(btn);

        map.setAspectRatio(aspectRatio, Number.POSITIVE_INFINITY);
        insetMap.setAspectRatio(aspectRatio, 0);

        if (exportWindow) {
            exportWindow.show();
            exportWindow.refreshPreviewPage();
        }
        else {
            if (this.isCropLockAspectRatio(btn)) {
                let size = this.getPaperSize(this.getCropAspectRatioCombo(btn).getValue());

                details.setSize({
                    height: size.getHeight(),
                    width: size.getWidth()
                });

                details.setOrientation(this.getCropOrientation(btn));
            }

            let paperWidth = details.getWidth(), 
                maxMapHeight = details.getHeight() - 300,
                maxMapWidth = Math.min(maxMapHeight / aspectRatio, paperWidth - 300);

            map.setDimensions({width: maxMapWidth / paperWidth});

            exportWindow = Ext.widget({
                xtype: 'vector-map-export-window',
                autoShow: true,
                details,
                map: croppingWindow.map,

                listeners: {
                    destroy: this.destroyExportViews,
                    'change:insetMap': this.onInsetMapChange,
                    'change:legend': this.onLegendChange,
                    'change:legendTheme': this.onLegendThemeChange,
                    'change:northArrow': this.onNorthArrowChange,
                    'change:orientation': this.onOrientationChange,
                    'change:pageSize': this.onPageSizeChange,
                    'change:scaleBar': this.onScaleBarChange,
                    'change:standardSize': this.onStandardSizeChange,
                    'click:addTextLabel': this.onAddTextLabelClick,
                    'click:continue': this.onContinueToExportClick,
                    'click:overlay': this.onOverlayClick,
                    'click:returnToCropping': this.onReturnToCroppingClick,
                    'click:textLabel': this.onTextLabelClick,
                    'inputblur:height': this.onDimensionChange,
                    'inputblur:width': this.onDimensionChange,
                    'move:textLabel': this.onTextLabelResizeOrMove,
                    'resize:textLabel': this.onTextLabelResizeOrMove,
                    'show:overlay': this.onOverlayShow,
                    'usermove:overlay': this.onOverlayUserMove,
                    'userresize:overlay': this.onOverlayUserResize,

                    scope: this
                }
            });
        }

        croppingWindow.hide();
    },

    onDeleteLabelClick(btn) {
        this.destroyTextLabel(btn);
    },

    onDimensionChange(field) {
        var details = this.getExport(field),
            fieldName = field.getName(),
            otherFieldName = Ext.String.toggle(fieldName, 'height', 'width'),
            aspectRatio = details.get(otherFieldName) / details.get(fieldName);

        details.setDisplaySize(fieldName, field.getValue());

        if (this.getLockAspectRatioButton(field).isLocked()) {
            details.setSize(otherFieldName, details.get(fieldName) * aspectRatio);
        }
    },

    async onFinishedClick(btn) {
        var deliveryWindow = this.getDeliveryOptionsWindow(btn),
            details = this.getExport(btn),
            emailParams = deliveryWindow.getEmailParams(),
            extension = deliveryWindow.getExtension(),
            isDownload = !deliveryWindow.isEmailChecked(),
            model = this.getMap(btn),
            modelState = model.getState(),
            params = SA.VectorMapExportParams.get({details, modelState});

        if (isDownload) {
            SA.msg('Your download will begin shortly.');
        }

        deliveryWindow.destroy();

        SA.Exports.export({
            resource: 'image',
            isDownload,
            model,
            extension,
            emailParams,
            params: {dpi: details.dpi, image: await SA.Canvas.toBase64(await SA.VectorMapExportPage.getCanvas(params))}
        });
    },

    onInsetMapChange(checkbox) {
        this.getExport(checkbox).getInsetMap().setVisible(checkbox.isChecked());
    },

    onLegendChange(checkbox) {
        var showLegend = checkbox.isChecked();

        this.getExport(checkbox).getLegend().setVisible(showLegend);
        this.getThemeRadioGroup(checkbox).setDisabled(!showLegend);
        this.getThemeRadioGroupLabel(checkbox).ifCls(!showLegend, 'sa-map-export-layout-row-label-disabled');
    },

    onLegendThemeChange(radioGroup, value) {
        this.getExport(radioGroup).getLegend().setTheme(value);
    },

    onNorthArrowChange(checkbox) {
        this.getExport(checkbox).getNorthArrow().setVisible(checkbox.isChecked());
    },

    onOrientationChange(radioGroup, value) {
        this.getExport(radioGroup).setOrientation(value);
    },

    onOverlayClick(overlay) {
        this.showOverlayEditWindow(overlay);
    },

    onOverlayHeightBlur(heightField) {
        var editWin = this.getOverlayEditWindow(heightField),
            page = this.getPreviewPage(editWin),
            details = page.details,
            overlay = editWin.overlay,
            record = overlay.getRecord(),
            pageHeight = details.getHeight(),
            pageWidth = details.getWidth(),
            min = Math.min,
            aspectRatio = record.getAspectRatio(),
            minDimensionValue = aspectRatio < 1 ? 300 * aspectRatio : 300,
            value = Math.max(heightField.getValue(), minDimensionValue),

            maxWidth = min(1, pageHeight / (pageWidth * aspectRatio)),

            overlayWidth = min(value / (pageWidth * aspectRatio), maxWidth);

        if (value !== editWin.dimensions.height) {
            if (overlayWidth > 0) {
                record.setDimensions({width: overlayWidth});
            }

            if (!editWin.isHidden()) {
                this.showOverlayEditWindow(overlay);
            }
        }
    },

    onOverlayLeftBlur(leftField) {
        var editWin = this.getOverlayEditWindow(leftField),
            value = leftField.getValue(),
            page = this.getPreviewPage(editWin),
            details = page.details,
            overlay = editWin.overlay,
            record = overlay.getRecord(),
            availableWidth = details.getWidth() * (1 - record.getWidth()),
            overlayX = Math.min(1, value / availableWidth);

        if (value !== editWin.dimensions.left) {
            if (availableWidth > 0) {
                record.setDimensions({x: overlayX});
            }

            if (!editWin.isHidden()) {
                this.showOverlayEditWindow(overlay);
            }
        }
    },

    onOverlayShow(overlay) {
        this.getPreviewPage(overlay).refreshZOrder();
    },

    onOverlayTopBlur(topField) {
        var editWin = this.getOverlayEditWindow(topField),
            value = topField.getValue(),
            page = this.getPreviewPage(editWin),
            details = page.details,
            overlay = editWin.overlay,
            record = overlay.getRecord(),
            availableHeight = details.getHeight() - record.getWidth() * details.getWidth() * record.getAspectRatio(),
            overlayY = Math.min(1, value / availableHeight);

        if (value !== editWin.dimensions.top) {
            if (availableHeight > 0) {
                record.setDimensions({y: overlayY});
            }

            if (!editWin.isHidden()) {
                this.showOverlayEditWindow(overlay);
            }
        }
    },

    onOverlayUserMove(overlay) {
        var record = overlay.getRecord(),
            pageSize = this.getPreviewPage(overlay).getSize(),
            overlaySize = overlay.getSize(),
            overlayPosition = overlay.getPosition(true),
            availableWidth = pageSize.width - overlaySize.width,
            availableHeight = pageSize.height - overlaySize.height,
            dimensions = {};

        if (availableWidth > 0) {
            dimensions.x = overlayPosition[0] / availableWidth;
        }

        if (availableHeight > 0) {
            dimensions.y = overlayPosition[1] / availableHeight;
        }

        record.setDimensions(dimensions);
    },

    onOverlayUserResize(overlay) {
        var record = overlay.getRecord(),
            pageSize = this.getPreviewPage(overlay).getSize(),
            overlaySize = overlay.getSize(),
            overlayPosition = overlay.getPosition(true),
            availableWidth = pageSize.width - overlaySize.width,
            availableHeight = pageSize.height - overlaySize.height,
            dimensions = {width: overlaySize.width / pageSize.width};

        if (availableWidth > 0) {
            dimensions.x = overlayPosition[0] / availableWidth;
        }

        if (availableHeight > 0) {
            dimensions.y = overlayPosition[1] / availableHeight;
        }

        record.setDimensions(dimensions);

        this.showOverlayEditWindow(overlay);
    },

    onOverlayWidthBlur(widthField) {
        var editWin = this.getOverlayEditWindow(widthField),
            page = this.getPreviewPage(editWin),
            details = page.details,
            overlay = editWin.overlay,
            record = overlay.getRecord(),
            pageHeight = details.getHeight(),
            pageWidth = details.getWidth(),
            min = Math.min,
            aspectRatio = record.getAspectRatio(),
            minDimensionValue = aspectRatio < 1 ? 300 : 300 * aspectRatio,
            value = Math.max(widthField.getValue(), minDimensionValue),

            maxWidth = min(1, pageHeight / (pageWidth * aspectRatio)),
            overlayWidth = min(value / pageWidth, maxWidth);

        if (value !== editWin.dimensions.width) {
            if (overlayWidth > 0) {
                record.setDimensions({width: overlayWidth});
            }

            if (!editWin.isHidden()) {
                this.showOverlayEditWindow(overlay);
            }
        }
    },

    onPageSizeChange(radioGroup, value) {
        this.getExport(radioGroup).setUnits(value);
    },

    onReturnToCroppingClick(btn) {
        var exportWindow = this.getExportWindow(btn),
            croppingWindow = this.getCroppingWindow(btn);

        croppingWindow.show();

        exportWindow.hide();
    },

    onReturnToLayoutClick(btn) {
        var exportWindow = this.getExportWindow(btn),
            deliveryWindow = this.getDeliveryOptionsWindow(btn);

        exportWindow.show();
        exportWindow.refreshPreviewPage();

        deliveryWindow.hide();
    },

    onScaleBarChange(checkbox) {
        this.getExport(checkbox).getScaleBar().setVisible(checkbox.isChecked());
    },

    onStandardSizeChange(combo) {
        var size = this.getPaperSize(combo.getValue()),
            details = this.getExport(combo),
            height, width;

        if (size) {
            height = size.getHeight();
            width = size.getWidth();

            if (this.getOrientation(combo) === 'portrait') {
                details.setSize({height, width});
            }
            else {
                details.setSize({
                    height: width,
                    width: height
                });
            }
        }
    },

    onTextBackgroundChange(colorChooser) {
        this.getTextOverlay(colorChooser).setBackground(colorChooser.getValue());
    },

    onTextColorChange(colorChooser) {
        this.getTextOverlay(colorChooser).setColor(colorChooser.getValue());
    },

    onTextLabelClick(label) {
        this.showTextLabelEditWindow(label);
    },

    onTextLabelResizeOrMove(label) {
        if (label.editWindow) {
            this.showTextLabelEditWindow(label);
        }
    },

    onTextLeftBlur(leftField) {
        var editWin = this.getLabelEditWindow(leftField),
            value = leftField.getValue(),
            page = this.getPreviewPage(editWin),
            details = page.details,
            label = editWin.textLabel,
            record = label.record,
            availableWidthProportion = 1 - label.getWidth() / page.getWidth(),
            availableWidth = details.getWidth() * availableWidthProportion,
            labelX = Math.min(1, value / availableWidth);

        if (value !== editWin.dimensions.left) {
            if (availableWidth > 0) {
                record.setDimensions({x: labelX});
            }

            if (!editWin.isHidden()) {
                this.showTextLabelEditWindow(label);
            }
        }
    },

    onTextSizeChange(combo) {
        this.getTextOverlay(combo).setSize(combo.getValue());
    },

    onTextStyleButtonClick(button) {
        this.getTextOverlay(button).setStyle(button.itemId, button.isPressed());
    },

    onTextTextBlur(textField) {
        var text = textField.getValue();

        if (text) {
            this.getTextOverlay(textField).setText(text);
        }
        else {
            this.destroyTextLabel(textField);
        }
    },

    onTextTopBlur(topField) {
        var editWin = this.getLabelEditWindow(topField),
            value = topField.getValue(),
            page = this.getPreviewPage(editWin),
            details = page.details,
            label = editWin.textLabel,
            record = label.record,
            availableHeightProportion = 1 - label.getHeight() / page.getHeight(),
            availableHeight = details.getHeight() * availableHeightProportion,
            labelY = Math.min(1, value / availableHeight);

        if (value !== editWin.dimensions.top) {
            if (availableHeight > 0) {
                record.setDimensions({y: labelY});
            }

            if (!editWin.isHidden()) {
                this.showTextLabelEditWindow(label);
            }
        }
    },

    onTextTypefaceChange(combo) {
        this.getTextOverlay(combo).setTypeface(combo.getValue());
    },

    registerChildEventListeners(window, selectors) {
        for (let [selector, listeners] of Object.entries(selectors)) {
            window.query(selector).forEach(cmp => cmp.on({scope: this, ...listeners}));
        }
    },

    showAlert(message) {
        Ext.widget({
            xtype: 'alert',
            message: Ext.Array.from(message).join(''),
            title: 'Unable to Export Map'
        });
    },

    showBeside(win, cmp) {
        var cmpPosition = cmp.getPosition(),
            localCmpPosition = cmp.getPosition(true),
            viewportWidth = Ext.dom.Element.getViewportWidth(),
            overlayWidth = cmp.getWidth(),
            winWidth,
            top;

        win.show();

        winWidth = win.getWidth();

        top = localCmpPosition[1] + (cmp.getHeight() - win.getHeight()) / 2;

        if (viewportWidth - overlayWidth < 2 * cmpPosition[0]) {
            win.showAt(localCmpPosition[0] - winWidth - 5, top);
        }
        else {
            win.showAt(localCmpPosition[0] + overlayWidth + 5, top);
        }
    },

    showCroppingWindow() {
        var size = SA.VectorMapExportCroppingWindow.computeFitSize(),
            win = Ext.widget({
                xtype: 'vector-map-export-cropping-window',
                autoShow: true,
                height: size.height,
                map: this.model,
                width: size.width,

                listeners: {
                    boxready: this.updateCropAspectRatio,
                    destroy: this.destroyExportViews,
                    'change:aspectRatio': this.updateCropAspectRatio,
                    'click:continue': this.onCropContinueClick,
                    'collapse:aspectRatio': this.updateCropAspectRatio,
                    'expand:aspectRatio': this.updateCropAspectRatio,

                    scope: this
                }
            });
    },

    showOverlayEditWindow(overlay) {
        var page = this.getPreviewPage(overlay),
            details = page.details,
            record = overlay.getRecord(),
            round = Math.round,
            pageWidth = details.getWidth(),
            pageHeight = details.getHeight(),
            overlayWidthInPixels = record.getWidth() * pageWidth,
            overlayHeightInPixels = overlayWidthInPixels * record.getAspectRatio(),
            win = overlay.editWindow;

        if (!win) {
            win = overlay.editWindow = page.add({
                listeners: {beforedestroy: this.beforeOverlayEditWindowDestroy, scope: this},
                overlay,
                overlayResizable: !!overlay.resizable,
                xtype: 'export-overlay-edit-window'
            });

            this.registerChildEventListeners(win, {
                '#height': {
                    blur: this.onOverlayHeightBlur
                },

                '#left': {
                    blur: this.onOverlayLeftBlur
                },

                '#top': {
                    blur: this.onOverlayTopBlur
                },

                '#width': {
                    blur: this.onOverlayWidthBlur
                }
            });
        }

        this.showBeside(win, overlay);

        win.dimensions = {
            height: round(overlayHeightInPixels),
            left: round(record.getX() * (pageWidth - overlayWidthInPixels)),
            top: round(record.getY() * (pageHeight - overlayHeightInPixels)),
            width: round(overlayWidthInPixels)
        };

        win.refresh();
    },

    showTextLabelEditWindow(label) {
        var page = this.getPreviewPage(label),
            details = page.details,
            record = label.record,
            round = Math.round,
            pageWidth = details.getWidth(),
            pageHeight = details.getHeight(),
            win = label.editWindow;

        if (!win) {
            win = label.editWindow = page.add({
                listeners: {beforedestroy: this.beforeTextEditWindowDestroy, scope: this},
                record,
                textLabel: label,
                xtype: 'export-label-edit-window'
            });

            this.registerChildEventListeners(win, {
                '#backgroundColor': {
                    change: this.onTextBackgroundChange
                },

                '#deleteLabelButton': {
                    click: this.onDeleteLabelClick
                },

                '#fontSizeCombo': {
                    change: this.onTextSizeChange
                },

                '#left': {
                    blur: this.onTextLeftBlur
                },

                'sa-toggle-button': {
                    click: this.onTextStyleButtonClick
                },

                '#text': {
                    blur: this.onTextTextBlur
                },

                '#textColor': {
                    change: this.onTextColorChange
                },

                '#top': {
                    blur: this.onTextTopBlur
                },

                '#typefaceCombo': {
                    change: this.onTextTypefaceChange
                }
            });
        }

        this.showBeside(win, label);

        win.dimensions = {
            left: round(pageWidth * record.getX() * (1 - label.getWidth() / page.getWidth())),
            top: round(pageHeight * record.getY() * (1 - label.getHeight() / page.getHeight()))
        };

        win.refresh();
    },

    updateCropAspectRatio(view) {
        var lock = this.isCropLockAspectRatio(view),
            cropBox = this.getCropBox(view);

        cropBox.setAspectRatio(lock ? this.getCropAspectRatio(view) : false);
    }
});